import React from 'react';
import { Message } from '@chatscope/chat-ui-kit-react';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { FileText } from '@lifeomic/chromicons';
import {
  AttachmentType,
  PostDetails_ActivePost_Fragment,
} from '../__generated__/api.graphql';
import { useIntl, defineMessages } from 'react-intl';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import {
  FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
  findCoding,
} from '@lifeomic/fhir-constants';

const messages = defineMessages({
  csvAttachment: {
    id: 'agentChat.chatAttachmentMessage.csvAttachment',
    defaultMessage: 'CSV File',
  },
  jsonAttachment: {
    id: 'agentChat.chatAttachmentMessage.jsonAttachment',
    defaultMessage: 'JSON File',
  },
  markdownAttachment: {
    id: 'agentChat.chatAttachmentMessage.markdownAttachment',
    defaultMessage: 'Markdown File',
  },
  pdfAttachment: {
    id: 'agentChat.chatAttachmentMessage.pdfAttachment',
    defaultMessage: 'PDF Document',
  },
  imageAttachment: {
    id: 'agentChat.chatAttachmentMessage.imageAttachment',
    defaultMessage: 'Image',
  },
  textAttachment: {
    id: 'agentChat.chatAttachmentMessage.textAttachment',
    defaultMessage: 'Text File',
  },
  genericAttachment: {
    id: 'agentChat.chatAttachmentMessage.genericAttachment',
    defaultMessage: 'File Attachment',
  },
  loadingAttachment: {
    id: 'agentChat.chatAttachmentMessage.loadingAttachment',
    defaultMessage: 'Loading attachment...',
  },
  viewImage: {
    id: 'agentChat.chatAttachmentMessage.viewImage',
    defaultMessage: 'View image',
  },
});

export type ChatAttachmentNode =
  PostDetails_ActivePost_Fragment['attachmentsV2']['edges'][number]['node'];

type AttachmentKind =
  | 'image'
  | 'pdf'
  | 'markdown'
  | 'csv'
  | 'json'
  | 'text'
  | 'generic';

type FileServiceAttachmentData = {
  downloadUrl?: string | null;
  contentType?: string | null;
  name?: string | null;
};

export type ChatAttachmentMessageProps = {
  post: PostDetails_ActivePost_Fragment;
  attachment: ChatAttachmentNode;
  currentUserId: string;
  patient: fhir.Patient;
  onOpenImage: (url: string) => void;
  onOpenPdf: (url: string) => void;
  onOpenFile: (url: string) => void;
};

const getAttachmentUrl = (attachment: ChatAttachmentNode) =>
  attachment.originalUrl ?? attachment.url ?? null;

const getAttachmentThumbnail = (attachment: ChatAttachmentNode) =>
  attachment.thumbnails[0] ?? null;

const getFileExtension = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const sanitizedValue = value.split('?')[0].toLowerCase();
  const lastDotIndex = sanitizedValue.lastIndexOf('.');

  return lastDotIndex === -1 ? '' : sanitizedValue.slice(lastDotIndex);
};

const inferContentType = ({
  attachment,
  fileName,
  downloadUrl,
}: {
  attachment: ChatAttachmentNode;
  fileName?: string | null;
  downloadUrl?: string | null;
}) => {
  const extension = getFileExtension(fileName) || getFileExtension(downloadUrl);

  if (extension === '.md' || extension === '.markdown') {
    return 'text/markdown';
  }

  if (extension === '.csv') {
    return 'text/csv';
  }

  if (extension === '.tsv') {
    return 'text/tab-separated-values';
  }

  if (extension === '.json') {
    return 'application/json';
  }

  if (
    extension === '.txt' ||
    extension === '.text' ||
    extension === '.log' ||
    extension === '.hl7' ||
    extension === '.grl'
  ) {
    return 'text/plain';
  }

  if (extension === '.pdf') {
    return 'application/pdf';
  }

  if (
    extension === '.jpg' ||
    extension === '.jpeg' ||
    extension === '.png' ||
    extension === '.gif' ||
    extension === '.webp'
  ) {
    return `image/${
      extension.slice(1) === 'jpg' ? 'jpeg' : extension.slice(1)
    }`;
  }

  if (attachment.type === AttachmentType.IMAGE) {
    return 'image/*';
  }

  if (attachment.type === AttachmentType.PDF) {
    return 'application/pdf';
  }

  return undefined;
};

const getAttachmentKind = ({
  attachment,
  contentType,
  fileName,
}: {
  attachment: ChatAttachmentNode;
  contentType?: string | null;
  fileName?: string | null;
}): AttachmentKind => {
  const normalizedContentType = contentType?.toLowerCase();
  const extension = getFileExtension(fileName);

  if (
    normalizedContentType?.startsWith('image/') ||
    attachment.type === AttachmentType.IMAGE
  ) {
    return 'image';
  }

  if (
    normalizedContentType === 'text/markdown' ||
    extension === '.md' ||
    extension === '.markdown'
  ) {
    return 'markdown';
  }

  if (
    normalizedContentType === 'text/csv' ||
    normalizedContentType === 'text/tab-separated-values' ||
    extension === '.csv' ||
    extension === '.tsv'
  ) {
    return 'csv';
  }

  if (normalizedContentType === 'application/json' || extension === '.json') {
    return 'json';
  }

  if (
    normalizedContentType?.startsWith('text/') ||
    extension === '.txt' ||
    extension === '.text' ||
    extension === '.log' ||
    extension === '.hl7' ||
    extension === '.grl'
  ) {
    return 'text';
  }

  if (
    normalizedContentType === 'application/pdf' ||
    extension === '.pdf' ||
    attachment.type === AttachmentType.PDF
  ) {
    return 'pdf';
  }

  return 'generic';
};

const useStyles = makeStyles(() => ({
  imageThumbnail: {
    maxWidth: 220,
    display: 'block',
  },
  fileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  fileButtonIcon: {
    flexShrink: 0,
  },
  fileLabelText: {
    textDecoration: 'underline',
  },
}));

const getDisplayLabel = (
  kind: AttachmentKind,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  switch (kind) {
    case 'image':
      return formatMessage(messages.imageAttachment);
    case 'csv':
      return formatMessage(messages.csvAttachment);
    case 'json':
      return formatMessage(messages.jsonAttachment);
    case 'markdown':
      return formatMessage(messages.markdownAttachment);
    case 'pdf':
      return formatMessage(messages.pdfAttachment);
    case 'text':
      return formatMessage(messages.textAttachment);
    default:
      return formatMessage(messages.genericAttachment);
  }
};

export const ChatAttachmentMessage: React.FC<ChatAttachmentMessageProps> = ({
  post,
  attachment,
  currentUserId,
  patient,
  onOpenImage,
  onOpenPdf,
  onOpenFile,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const authorUserId = post.author?.userId ?? '';
  const direction: 'outgoing' | 'incoming' =
    authorUserId === currentUserId ? 'outgoing' : 'incoming';
  const { code: projectId } = findCoding(patient?.meta?.tag as fhir3.Coding[], {
    system: FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
    requireCode: true,
  });
  const isFileServiceAttachment = attachment.subType === 'file-service';

  const fileQuery = apiHooks.useAPIQuery(
    'GET /v1/fhir/files/projects/:projectId/files/:fileId',
    {
      projectId,
      subject: patient.id!,
      fileId: attachment.externalId,
      include: 'downloadUrl',
      includeContentDisposition: false,
    },
    {
      enabled: Boolean(
        isFileServiceAttachment && projectId && attachment.externalId,
      ),
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  ) as {
    data?: FileServiceAttachmentData;
    isLoading: boolean;
  };

  const resolvedFileName = attachment.fileName ?? fileQuery.data?.name ?? null;
  const resolvedUrl =
    getAttachmentUrl(attachment) ?? fileQuery.data?.downloadUrl;
  const contentType =
    fileQuery.data?.contentType ??
    inferContentType({
      attachment,
      fileName: resolvedFileName,
      downloadUrl: resolvedUrl,
    });
  const attachmentKind = getAttachmentKind({
    attachment,
    contentType,
    fileName: resolvedFileName,
  });
  const displayLabel = getDisplayLabel(attachmentKind, formatMessage);
  const thumbnail = getAttachmentThumbnail(attachment);

  const handleOpenAttachment = () => {
    if (!resolvedUrl) {
      return;
    }

    if (attachmentKind === 'image') {
      onOpenImage(resolvedUrl);
      return;
    }

    if (attachmentKind === 'pdf') {
      onOpenPdf(resolvedUrl);
      return;
    }

    onOpenFile(resolvedUrl);
  };

  const messageModel = {
    message: '',
    sentTime: post.createdAt,
    sender: post.author?.profile.displayName ?? '',
    direction,
    position: 'single' as const,
  };

  if (attachmentKind === 'image' && thumbnail) {
    return (
      <Message model={messageModel}>
        <Message.CustomContent>
          <ButtonUnstyled
            disabled={!resolvedUrl}
            onClick={handleOpenAttachment}
            title={formatMessage(messages.viewImage)}
          >
            <img alt="" src={thumbnail} className={classes.imageThumbnail} />
          </ButtonUnstyled>
        </Message.CustomContent>
      </Message>
    );
  }

  return (
    <Message model={messageModel}>
      <Message.CustomContent>
        <ButtonUnstyled
          className={classes.fileButton}
          disabled={!resolvedUrl}
          onClick={handleOpenAttachment}
          title={`Open ${displayLabel}`}
        >
          <FileText className={classes.fileButtonIcon} />
          <Text
            color={direction === 'outgoing' ? 'inverse' : 'default'}
            className={classes.fileLabelText}
          >
            {fileQuery.isLoading && !resolvedUrl
              ? formatMessage(messages.loadingAttachment)
              : displayLabel}
          </Text>
        </ButtonUnstyled>
      </Message.CustomContent>
    </Message>
  );
};
